import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import type { Effect, EffectType, EffectData } from '@multipack/protocol';

interface ActiveEffect {
  id: string;
  effect: EffectType;
  data?: EffectData;
  startTime: number;
}

interface EffectContextValue {
  /** Manually trigger an effect locally */
  trigger: (effect: EffectType, data?: EffectData) => void;
}

const EffectContext = createContext<EffectContextValue | null>(null);

/** Custom event name for effect events */
const EFFECT_EVENT = 'multipack:effect';

/** Dispatch an effect event (called from message handler) */
export function dispatchEffect(effect: Effect): void {
  window.dispatchEvent(
    new CustomEvent(EFFECT_EVENT, {
      detail: effect,
    })
  );
}

interface EffectProviderProps {
  children: ReactNode;
  /** Player ID for target filtering */
  playerId?: string;
  /** Known latency to server for synchronization (ms) */
  latencyMs?: number;
}

/**
 * Provider that handles synchronized effects on mobile controllers.
 * Focuses on vibration and simple visual effects suitable for phones.
 */
export function EffectProvider({ children, playerId, latencyMs = 0 }: EffectProviderProps) {
  const [activeEffects, setActiveEffects] = useState<ActiveEffect[]>([]);

  // Check if this client should show an effect based on target
  const shouldShowEffect = useCallback(
    (target: Effect['target']): boolean => {
      if (target === 'all') return true;
      if (target === 'controllers') return true;
      if (typeof target === 'string' && target === playerId) return true;
      if (Array.isArray(target) && playerId && target.includes(playerId)) return true;
      // Host-only effects shouldn't show on controllers
      if (target === 'host') return false;
      return false;
    },
    [playerId]
  );

  // Trigger an effect
  const triggerEffect = useCallback((effectType: EffectType, data?: EffectData) => {
    const id = `effect-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const duration = data?.duration ?? 3000;

    // Handle vibration immediately
    if (effectType === 'vibrate' || effectType === 'celebrate' || effectType === 'alert') {
      if ('vibrate' in navigator) {
        const vibrateDuration = effectType === 'alert' ? [100, 50, 100] : duration;
        navigator.vibrate(vibrateDuration);
      }
    }

    setActiveEffects((prev) => [...prev, { id, effect: effectType, data, startTime: Date.now() }]);

    // Auto-remove after duration
    setTimeout(() => {
      setActiveEffects((prev) => prev.filter((e) => e.id !== id));
    }, duration);
  }, []);

  // Listen for effect events
  useEffect(() => {
    const handleEffect = (event: CustomEvent<Effect>) => {
      const effect = event.detail;
      if (!shouldShowEffect(effect.target)) return;

      // Calculate delay for synchronization
      const now = Date.now();
      const targetTime = effect.timestamp;
      const delay = Math.max(0, targetTime - now + latencyMs);

      if (delay > 0) {
        setTimeout(() => triggerEffect(effect.effect, effect.data), delay);
      } else {
        triggerEffect(effect.effect, effect.data);
      }
    };

    window.addEventListener(EFFECT_EVENT, handleEffect as EventListener);
    return () => window.removeEventListener(EFFECT_EVENT, handleEffect as EventListener);
  }, [shouldShowEffect, triggerEffect, latencyMs]);

  // Context value for manual triggers
  const contextValue: EffectContextValue = {
    trigger: triggerEffect,
  };

  // Get flash color based on effect type
  const getFlashStyle = (effect: ActiveEffect): React.CSSProperties | null => {
    switch (effect.effect) {
      case 'screenFlash':
      case 'celebrate':
        return {
          backgroundColor: effect.data?.color || 'rgba(255, 215, 0, 0.3)',
        };
      case 'alert':
        return {
          backgroundColor: 'rgba(239, 68, 68, 0.4)',
        };
      default:
        return null;
    }
  };

  return (
    <EffectContext.Provider value={contextValue}>
      {children}

      {/* Render active visual effects */}
      {activeEffects.map((active) => {
        const style = getFlashStyle(active);
        if (!style) return null;

        return (
          <div
            key={active.id}
            className="fixed inset-0 pointer-events-none z-[100] animate-flash"
            style={style}
            aria-hidden="true"
          />
        );
      })}
    </EffectContext.Provider>
  );
}

/** Hook to access effect triggers */
export function useEffects(): EffectContextValue {
  const context = useContext(EffectContext);
  if (!context) {
    throw new Error('useEffects must be used within an EffectProvider');
  }
  return context;
}
